import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../CompanyContext';
import { loansAPI } from '../../services/api';
import { Loan, LoanStatus } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  CreditCardIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const LoansList: React.FC = () => {
  const { selectedCompany } = useCompany();
  const { t } = useTranslation();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (selectedCompany) {
      fetchLoans();
    }
  }, [selectedCompany, statusFilter]);

  const fetchLoans = async () => {
    if (!selectedCompany) return;
    
    try {
      setLoading(true);
      setError(null);
      let data: Loan[];
      
      if (searchTerm.trim()) {
        // For now, get all loans and filter client-side
        data = await loansAPI.getByCompany(selectedCompany.id);
        data = data.filter(loan => 
          loan.borrower?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          loan.id.toLowerCase().includes(searchTerm.toLowerCase())
        );
      } else {
        data = await loansAPI.getByCompany(selectedCompany.id);
      }
      
      // Apply status filter
      if (statusFilter !== 'all') {
        data = data.filter(loan => loan.status === statusFilter);
      }
      
      setLoans(data);
    } catch (err) {
      setError(t('loans.errorTitle'));
      console.error('Error fetching loans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLoans();
  };

  const getStatusIcon = (status: LoanStatus) => {
    switch (status) {
      case LoanStatus.ACTIVE:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case LoanStatus.COMPLETED:
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      case LoanStatus.DEFAULTED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case LoanStatus.CANCELLED:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: LoanStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border";
    
    switch (status) {
      case LoanStatus.ACTIVE:
        return `${baseClasses} bg-green-100 text-green-800 border-green-200`;
      case LoanStatus.COMPLETED:
        return `${baseClasses} bg-blue-100 text-blue-800 border-blue-200`;
      case LoanStatus.DEFAULTED:
        return `${baseClasses} bg-red-100 text-red-800 border-red-200`;
      case LoanStatus.CANCELLED:
        return `${baseClasses} bg-gray-100 text-gray-800 border-gray-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 border-gray-200`;
    }
  };

  const formatCurrency = (amount: number, currencyId?: string) => {
    // For now, format as USD - in a real app, you'd use the currency from location
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString();
  };

  if (!selectedCompany) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('common.noCompanySelected')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('common.selectCompanyToView')} {t('nav.loans').toLowerCase()}.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('loans.errorTitle')}</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={fetchLoans}
            className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">
            {t('loans.title')} ({loans.length})
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {t('loans.subtitle')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Link
            to="/loans/new"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            {t('loans.new')}
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <form onSubmit={handleSearch} className="flex rounded-md shadow-sm">
              <div className="relative flex flex-grow items-stretch focus-within:z-10">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full rounded-none rounded-l-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  placeholder={t('loans.search')}
                />
              </div>
              <button
                type="submit"
                className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
{t('actions.search')}
              </button>
            </form>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              <option value="all">{t('loans.filters.allStatus')}</option>
              <option value={LoanStatus.ACTIVE}>{t('loans.status.ACTIVE')}</option>
              <option value={LoanStatus.COMPLETED}>{t('loans.status.COMPLETED')}</option>
              <option value={LoanStatus.DEFAULTED}>{t('loans.status.DEFAULTED')}</option>
              <option value={LoanStatus.CANCELLED}>{t('loans.status.CANCELLED')}</option>
            </select>
          </div>

          <div></div> {/* Empty column for spacing */}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
        {loans.length === 0 ? (
          <div className="text-center py-12">
            <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm ? t('loans.noResults') : t('loans.noLoans')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? t('loans.tryDifferentSearch') : t('loans.getStarted')}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <Link
                  to="/loans/new"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                  {t('loans.new')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('loans.list.borrower')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('loans.list.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('loans.list.balance')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('loans.list.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t('loans.list.dueDate')}
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loans.map((loan) => (
                <tr key={loan.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {loan.borrower?.name || t('loans.list.unknownBorrower')}
                        </div>
                        <div className="text-sm text-gray-500">
                          {loan.borrower?.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCurrency(loan.principalAmount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatCurrency(loan.currentBalance)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatCurrency(loan.totalPaid)} {t('loans.list.paid')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(loan.status)}>
                      {getStatusIcon(loan.status)}
                      <span className="ml-1">{t(`loans.status.${loan.status.toUpperCase()}`)}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {loan.nextPaymentDue ? formatDate(loan.nextPaymentDue) : t('common.na')}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <div className="flex items-center justify-end space-x-2">
                      <Link
                        to={`/loans/${loan.id}`}
                        className="text-indigo-600 hover:text-indigo-900"
                        title={t('actions.view')}
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/loans/${loan.id}/edit`}
                        className="text-indigo-600 hover:text-indigo-900"
                        title={t('actions.edit')}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                      {/* Payment button - we'll implement this later */}
                      <button
                        className="text-green-600 hover:text-green-900"
                        title={t('loans.recordPayment')}
                      >
                        <CurrencyDollarIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LoansList;
